import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    NgZone,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WelcomeSplashService } from './welcome-splash.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { TranslationService } from '@core/translation/translation.service';

type TimeOfDay = 'morning' | 'day' | 'evening' | 'night';

type Phase = 'hidden' | 'enter' | 'visible' | 'exit';

/**
 * "Horizont-Sonnenaufgang"-Begrüßung nach Keycloak-Login.
 *
 * Tageszeit-abhängige Szene (Himmel, Sonne/Mond, Wasserreflexion) plus
 * tippende Begrüßung mit dem Vornamen des Nutzers. Der Splash wird nach
 * dem Exit komplett aus dem DOM entfernt.
 *
 * Gesamtdauer bei Default-Timings: ENTER (900ms) + HOLD (3200ms) + EXIT
 * (1700ms) ≈ 5,8s. Bei langen Namen wird HOLD dynamisch erweitert, damit
 * der Tipp-Effekt vollständig läuft, bevor der Exit beginnt.
 */
@Component({
    selector: 'u2b-welcome-splash',
    templateUrl: './welcome-splash.component.html',
    styleUrls: ['./welcome-splash.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WelcomeSplashComponent implements OnInit, OnDestroy {

    // --- Anzeigezustand ---
    phase: Phase = 'hidden';
    timeOfDay: TimeOfDay = 'day';
    headline = '';
    greetingLine = '';
    firstName = '';
    displayedName = '';
    /** Gecachtes aria-label – wird einmalig in _start() gesetzt, nicht bei jedem Tipp-Schritt. */
    ariaLabel = '';

    // --- Timing (ms) ---
    private readonly ENTER_MS = 900;
    private readonly HOLD_MS = 3200;
    private readonly EXIT_MS = 1700;
    private readonly TYPE_DELAY_MS = 1000;
    private readonly TYPE_INTERVAL_MS = 55;
    /** 
     * Puffer nach Ende des Tipp-Effekts, bevor der Exit beginnt.
     * 1500ms entspricht der durchschnittlichen Lesezeit für eine kurze
     * Begrüßung (8-12 Zeichen), basierend auf UI-Feedback-Standards.
     */
    private readonly TYPE_BUFFER_MS = 1500;

    private readonly _unsub$ = new Subject<void>();
    private _timeouts: number[] = [];
    private _reducedMotion = false;
    private _reducedMotionQuery: MediaQueryList | null = null;
    private _reducedMotionHandler: ((e: MediaQueryListEvent) => void) | null = null;

    @HostBinding('class.is-visible')
    get cssVisible(): boolean {
        return this.phase !== 'hidden';
    }

    @HostBinding('class.is-exit')
    get cssExit(): boolean {
        return this.phase === 'exit';
    }

    @HostBinding('attr.data-time-of-day')
    get attrTimeOfDay(): TimeOfDay {
        return this.timeOfDay;
    }

    /**
     * Logo-Variante abhängig von der Szene (Kontrast-Optimierung).
     * Originale freigestellte Corporate-PNGs mit korrekt ausgestanzten
     * Counter-Formen (p, o, a, e, ö, g transparent). Das Logo darf NIE
     * nachgebaut oder als einfaches SVG rekonstruiert werden – nur die
     * offiziellen Assets verwenden.
     */
    get logoSrc(): string {
        const useLight = this.timeOfDay === 'evening' || this.timeOfDay === 'night';
        return useLight
            ? 'assets/images/logos/up2boat-claim-white.png'
            : 'assets/images/logos/up2boat-claim-black.png';
    }

    /** Gibt true zurück, wenn ein Name zum Anzeigen vorhanden ist. */
    get hasName(): boolean {
        return !!this.firstName;
    }

    /** Text für die Begrüßungszeile ohne Namen (z.B. "Guten Morgen!"). */
    get greetingLineNoName(): string {
        return this.greetingLine.replace(/[,!.\s]+$/, '') + '!';
    }

    constructor(
        private _splashService: WelcomeSplashService,
        private _tolgee: TolgeeService,
        private _cdr: ChangeDetectorRef,
        private _ngZone: NgZone,
    ) {
    }

    ngOnInit(): void {
        this._reducedMotion = this._prefersReducedMotion();
        this._registerReducedMotionListener();

        this._splashService.show$
            .pipe(takeUntil(this._unsub$))
            .subscribe(payload => {
                if (payload) {
                    this._start(payload.firstName);
                }
            });
    }

    ngOnDestroy(): void {
        this._clearTimeouts();
        this._cleanupReducedMotionListener();
        this._unsub$.next();
        this._unsub$.complete();
    }

    // --- Ablauf ---

    private _start(firstName: string): void {
        this._clearTimeouts();

        this.firstName = (firstName ?? '').trim();
        // Vornamen-Anzeige startet leer und wird über den Tipp-Effekt
        // aufgebaut. Ohne Namen übernimmt greetingLineNoName die Darstellung.
        this.displayedName = '';
        this.timeOfDay = this._resolveTimeOfDay(new Date());
        this.greetingLine = this._resolveGreeting(this.timeOfDay);
        this.headline = this._resolveHeadline();
        // aria-label einmalig setzen – NICHT als reaktiver Getter, damit
        // Screenreader nicht bei jedem Tipp-Schritt erneut vorliest.
        const greeting = this.hasName
            ? `${this.greetingLine} ${this.firstName}`
            : this.greetingLineNoName;
        this.ariaLabel = `${greeting}. ${this.headline}.`;
        this.phase = 'enter';
        this._cdr.markForCheck();

        // Unter prefers-reduced-motion deutlich kürzere Dauer und kein Tippeffekt.
        const enterMs = this._reducedMotion ? 0 : this.ENTER_MS;
        const exitMs = this._reducedMotion ? 200 : this.EXIT_MS;
        const holdMs = this._computeHoldMs();

        this._schedule(() => {
            this.phase = 'visible';
            this._cdr.markForCheck();
        }, enterMs);

        if (!this._reducedMotion && this.hasName) {
            this._schedule(
                () => this._runOutsideAngular(() => this._typeName()),
                this.TYPE_DELAY_MS,
            );
        } else if (this.hasName) {
            // Reduced-motion: Name sofort vollständig anzeigen.
            this.displayedName = this.firstName;
        }

        const totalHold = enterMs + holdMs;
        this._schedule(() => {
            this.phase = 'exit';
            this._cdr.markForCheck();
        }, totalHold);

        this._schedule(() => {
            this.phase = 'hidden';
            this.displayedName = '';
            this._cdr.markForCheck();
            this._splashService.dismiss();
        }, totalHold + exitMs);
    }

    /**
     * Berechnet die Halte-Dauer so, dass der Tipp-Effekt auf jeden Fall
     * vollständig abläuft, bevor der Exit beginnt (M1-Fix).
     */
    private _computeHoldMs(): number {
        if (this._reducedMotion || !this.hasName) {
            return this.HOLD_MS;
        }
        const typingMs = this.TYPE_DELAY_MS
            + this.firstName.length * this.TYPE_INTERVAL_MS
            + this.TYPE_BUFFER_MS;
        return Math.max(this.HOLD_MS, typingMs);
    }

    private _typeName(): void {
        const name = this.firstName;
        if (!name) {
            return;
        }
        let i = 0;
        const step = () => {
            // Abbrechen, sobald der Splash die Exit-Phase erreicht hat.
            if (this.phase === 'exit' || this.phase === 'hidden') {
                return;
            }
            i += 1;
            this.displayedName = name.slice(0, i);
            this._cdr.markForCheck();  // läuft bereits in NgZone durch _schedule()
            if (i < name.length) {
                this._schedule(() => step(), this.TYPE_INTERVAL_MS);
            }
        };
        // Initialen Call über _schedule() starten → alle Timeouts konsistent
        this._schedule(() => step(), 0);
    }

    private _resolveTimeOfDay(now: Date): TimeOfDay {
        const h = now.getHours();
        if (h >= 5 && h < 11) {
            return 'morning';
        }
        if (h >= 11 && h < 18) {
            return 'day';
        }
        if (h >= 18 && h < 22) {
            return 'evening';
        }
        return 'night';
    }

    private _resolveGreeting(tod: TimeOfDay): string {
        const key = `welcomeSplash.greeting.${tod}`;
        const fallback = this._fallbackGreeting(tod, this._tolgee.getLanguage());
        return TranslationService.translate(key, undefined, fallback);
    }

    private _resolveHeadline(): string {
        const lang = this._tolgee.getLanguage();
        const fallback = (lang || '').toLowerCase().startsWith('en')
            ? 'Welcome to Up2Boat'
            : 'Willkommen bei Up2Boat';
        return TranslationService.translate('welcomeSplash.headline', undefined, fallback);
    }

    private _fallbackGreeting(tod: TimeOfDay, lang: string): string {
        const isEn = (lang || '').toLowerCase().startsWith('en');
        const greetings = isEn
            ? { morning: 'Good morning,', day: 'Good afternoon,', evening: 'Good evening,', night: 'Good evening,' }
            : { morning: 'Guten Morgen,', day: 'Guten Tag,', evening: 'Guten Abend,', night: 'Guten Abend,' };
        return greetings[tod];
    }

    private _prefersReducedMotion(): boolean {
        try {
            return typeof window !== 'undefined'
                && typeof window.matchMedia === 'function'
                && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch {
            return false;
        }
    }

    private _registerReducedMotionListener(): void {
        try {
            if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
                return;
            }
            this._reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
            // Handler-Referenz speichern, damit removeEventListener dieselbe
            // Funktion erhält und den Listener tatsächlich entfernt.
            this._reducedMotionHandler = (e: MediaQueryListEvent) => {
                this._reducedMotion = e.matches;
                // Hinweis: Bei Änderung während laufender Animation wird die
                // aktuelle Animation NICHT abgebrochen (zu komplex), aber
                // zukünftige Splash-Aufrufe respektieren die neue Einstellung.
            };
            // Moderne Browser nutzen addEventListener, ältere addListener
            if (this._reducedMotionQuery.addEventListener) {
                this._reducedMotionQuery.addEventListener('change', this._reducedMotionHandler);
            } else if ((this._reducedMotionQuery as any).addListener) {
                (this._reducedMotionQuery as any).addListener(this._reducedMotionHandler);
            }
        } catch {
            // MediaQuery-Listener nicht verfügbar – kein Problem, initial value greift
        }
    }

    private _cleanupReducedMotionListener(): void {
        if (!this._reducedMotionQuery || !this._reducedMotionHandler) {
            return;
        }
        try {
            // Dieselbe Referenz wie bei addEventListener übergeben
            if (this._reducedMotionQuery.removeEventListener) {
                this._reducedMotionQuery.removeEventListener('change', this._reducedMotionHandler);
            } else if ((this._reducedMotionQuery as any).removeListener) {
                (this._reducedMotionQuery as any).removeListener(this._reducedMotionHandler);
            }
        } catch {
            // Graceful failure bei Cleanup
        }
        this._reducedMotionQuery = null;
        this._reducedMotionHandler = null;
    }

    private _runOutsideAngular(fn: () => void): void {
        this._ngZone.runOutsideAngular(fn);
    }

    private _schedule(fn: () => void, delay: number): void {
        const id = window.setTimeout(() => this._ngZone.run(fn), delay);
        this._timeouts.push(id);
    }

    private _clearTimeouts(): void {
        this._timeouts.forEach(id => window.clearTimeout(id));
        this._timeouts = [];
    }
}
